import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n/I18nProvider';
import { useBackNavigation } from '../context/BackNavigationContext';
import { useLayout } from '../layout/useLayout';
import { AppShell } from '../layout/shell/AppShell';
import { BottomDock } from '../layout/shell/BottomDock';
import { ScreenViewport } from '../layout/shell/ScreenViewport';
import { LayoutScreenKey, getScreenLayout, resolveContentWidthClass } from '../layout/layouts';

interface MobilePageShellProps {
  title?: string;
  subtitle?: string;
  backPath?: string;
  children: React.ReactNode;
  footerSticky?: React.ReactNode;
  footer?: React.ReactNode;
  footerCollapsible?: boolean;
  centered?: boolean;
  scroll?: boolean;
  className?: string;
  headerExtras?: React.ReactNode;
  headerBelow?: React.ReactNode;
  hideHeader?: boolean;
  screenKey?: LayoutScreenKey;
  contentMaxWidthClass?: string;
  compactSpacing?: boolean;
  hideBackButton?: boolean;
  onBack?: () => void;
  syncBackHandler?: boolean;
}

const MobilePageShell: React.FC<MobilePageShellProps> = ({
  title,
  subtitle,
  backPath,
  children,
  footerSticky,
  footer,
  footerCollapsible = true,
  centered = false,
  scroll = true,
  className = '',
  headerExtras,
  headerBelow,
  hideHeader = false,
  screenKey,
  contentMaxWidthClass,
  compactSpacing = false,
  hideBackButton = false,
  onBack,
  syncBackHandler = true,
}) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { layoutId } = useLayout();
  const { setHandler: setBackHandler } = useBackNavigation();
  const [footerHeight, setFooterHeight] = React.useState(0);
  const footerRef = React.useRef<HTMLDivElement | null>(null);
  const contentPaddingY = centered ? 'pb-4' : (compactSpacing ? 'pb-2' : 'pb-4');
  const hasHeaderContent = !hideHeader && (title || subtitle || backPath || onBack || headerExtras || headerBelow);
  const hasFooterContent = Boolean(footerSticky || footer);
  const hasBottomChrome = hasHeaderContent || hasFooterContent;
  const [footerCollapsed, setFooterCollapsed] = React.useState(footerCollapsible);
  const footerExpanded = !footerCollapsed || !footerCollapsible;
  const screenPreset = screenKey ? getScreenLayout(layoutId, screenKey) : null;
  const resolvedContentMaxWidthClass = contentMaxWidthClass ?? (screenPreset ? resolveContentWidthClass(screenPreset.contentWidth) : undefined);
  const toggleLabel = footerCollapsed ? 'Pokaż panel' : 'Zwiń panel';
  const floatingPadding = 'var(--floating-viewport-reserve)';
  const floatingViewportInset = 'var(--floating-viewport-reserve)';
  const topPadding = hideHeader ? '0px' : (centered ? '1rem' : (compactSpacing ? '0.5rem' : '1rem'));
  const safeAreaTop = 'var(--app-safe-area-top)';
  const viewportBottomInset = hasBottomChrome
    ? `calc(${footerHeight}px + ${floatingViewportInset})`
    : undefined;
  const contentStyle = {
    paddingBottom: floatingPadding,
    paddingTop: `calc(${safeAreaTop} + var(--top-gap) + ${topPadding})`,
    paddingInline: 'var(--layout-screen-padding-x)',
    '--floating-content-pad': floatingPadding,
    ...(!resolvedContentMaxWidthClass ? { maxWidth: 'var(--layout-content-max-width)' } : {}),
  } as React.CSSProperties;
  const innerContentClassName = `w-full ${contentPaddingY} ${resolvedContentMaxWidthClass ?? ''} mx-auto content-stack ${centered ? 'content-stack-centered' : ''}`.trim();
  const dockContainerStyle = resolvedContentMaxWidthClass
    ? undefined
    : ({ maxWidth: 'var(--layout-content-max-width)' } as React.CSSProperties);

  React.useEffect(() => {
    if (!footerCollapsible) {
      setFooterCollapsed(false);
    }
  }, [footerCollapsible]);

  React.useEffect(() => {
    if (!hasBottomChrome) return;
    const el = footerRef.current;
    if (!el) return;
    const measure = () => {
      try {
        const rect = el.getBoundingClientRect();
        setFooterHeight(rect.height || 0);
      } catch {}
    };
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => measure());
      ro.observe(el);
      return () => ro.disconnect();
    }
    const id = window.setInterval(measure, 300);
    return () => window.clearInterval(id);
  }, [hasBottomChrome]);

  React.useEffect(() => {
    if (!syncBackHandler) return;
    if (!backPath && !onBack) return;
    const handler = () => {
      if (onBack) {
        onBack();
        return;
      }
      if (backPath) {
        navigate(backPath);
      }
    };
    setBackHandler(handler);
    return () => {
      setBackHandler(null);
    };
  }, [backPath, navigate, onBack, setBackHandler, syncBackHandler]);

  return (
    <AppShell
      className={className}
      layoutId={layoutId}
      footerHeight={hasBottomChrome ? footerHeight : undefined}
    >
      <ScreenViewport
        title={title}
        centered={centered}
        scroll={scroll}
        contentClassName={innerContentClassName}
        contentStyle={contentStyle}
        bottomInset={viewportBottomInset}
        ariaLabel={title ? t('layout.mainWithTitle', { title }) : t('layout.main')}
      >
        {children}
      </ScreenViewport>
      {hasBottomChrome && (
        <BottomDock footerRef={footerRef} expanded={footerExpanded}>
            <div
              className={`${resolvedContentMaxWidthClass ?? ''} mx-auto w-full flex flex-col ${footerCollapsed ? 'gap-1' : 'gap-2'}`.trim()}
              style={dockContainerStyle}
            >
              {footerSticky && (
                <div className="flex flex-col gap-3">
                  {footerSticky}
                </div>
              )}
              {footerCollapsible && (
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => setFooterCollapsed((v) => !v)}
                    className={`flex items-center justify-center rounded-full bg-brand-bg-alt/60 border border-brand-border text-accent shadow-xs hover:bg-brand-bg-alt/80 transition-colors ${footerCollapsed ? 'w-12 h-5' : 'w-10 h-6'}`}
                    aria-label={toggleLabel}
                    data-testid="footer-toggle"
                  >
                    <svg
                      aria-hidden="true"
                      className="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      {footerCollapsed
                        ? <path d="M6 14l6-6 6 6" />
                        : <path d="M6 10l6 6 6-6" />}
                    </svg>
                  </button>
                </div>
              )}
              {footerExpanded && (
                <div className="flex flex-col gap-3">
                  {hasHeaderContent && (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-3">
                        {(!hideBackButton && (backPath || onBack)) ? (
                          <button
                            onClick={() => (onBack ? onBack() : backPath ? navigate(backPath) : undefined)}
                            className="text-accent self-center flex items-center justify-center transform"
                          >
                            <svg aria-hidden="true" className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M15 5l-7 7 7 7" />
                            </svg>
                            <span className="sr-only">{t('common.back')}</span>
                          </button>
                        ) : (
                          <span aria-hidden="true" />
                        )}
                        <div className="flex-1 flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-wrap">
                            {title && <h1 className="text-xl font-bold text-accent mb-0">{title}</h1>}
                            {subtitle && <p className="text-brand-text opacity-70 text-sm mt-0">{subtitle}</p>}
                          </div>
                          {headerExtras}
                        </div>
                      </div>
                      {headerBelow && (
                        <div className="flex items-center justify-end gap-3">
                          <div className="flex-1 flex justify-end">
                            {headerBelow}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {footer}
                </div>
              )}
            </div>
        </BottomDock>
      )}
    </AppShell>
  );
};

export default MobilePageShell;
